import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { AlertCircle, Plus, CheckCircle, Info } from 'lucide-react';
import { toast } from 'sonner';
import DuplicateWarning from './DuplicateWarning';
import { useUser } from '../context/UserContext';
import StateChangeDialog from './StateChangeDialog';
import QuickCompanyDialog from './QuickCompanyDialog';
import QuickContactDialog from './QuickContactDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGES = ['Detectada', 'Propuesta', 'Presentada', 'Ganada', 'Perdida', 'Descartada'];

// Iter 94: transiciones prohibidas entre estados (según petición usuario).
// Descartada → Descartada: no-op no deseado. Ganada → Perdida: no permitido.
// Ganada/Perdida → Descartada: no permitido (ya se cerró el ciclo).
const FORBIDDEN_TRANSITIONS = {
  Descartada: ['Descartada'],
  Ganada:     ['Perdida', 'Descartada'],
  Perdida:    ['Descartada'],
};

const getAllowedStages = (fromStage) => {
  if (!fromStage) return STAGES;
  const forbidden = FORBIDDEN_TRANSITIONS[fromStage] || [];
  // Se mantiene el estado actual (aunque esté en la lista prohibida) para que
  // el Select pueda seguir mostrándolo como valor seleccionado sin romper el UI.
  return STAGES.filter((s) => s === fromStage || !forbidden.includes(s));
};
const RELEVANCE_OPTIONS = ['Alta', 'Media', 'Baja'];
const PROBABILITY_OPTIONS = ['Alta', 'Media', 'Baja'];

// Feb 2026 (PR-B B2): closed taxonomy for discard reasons.
// Kept in sync with backend models/opportunity.py::DISCARD_REASON_KEYS.
const DISCARD_REASON_OPTIONS = [
  { key: 'sin_capacidad_tecnica',  label: 'Sin capacidad técnica o sin perfil disponible' },
  { key: 'precio_no_competitivo',  label: 'Precio no competitivo (no alcanzamos la baja)' },
  { key: 'sin_recursos_o_plazo',   label: 'Sin capacidad de recursos o plazo incompatible' },
  { key: 'alcance_no_estrategico', label: 'Alcance no estratégico para TDS' },
  { key: 'riesgo_contractual',     label: 'Riesgo contractual, solvencia o garantías' },
  { key: 'otra_unidad_typsa',      label: 'Se presenta otra unidad de TYPSA' },
  { key: 'conflicto_interes',      label: 'Conflicto de interés o con cliente existente' },
  { key: 'lote_no_interesante',    label: 'Lote o alcance parcial no interesante' },
  { key: 'deteccion_tardia',       label: 'Detección tardía, sin tiempo' },
  { key: 'otro',                   label: 'Otro' },
];


// Tipos de servicios disponibles
const SERVICE_TYPES = [
  'BIM',
  'GIS',
  'Gobernanza del Dato',
  'Inteligencia Artificial',
  'Software a Medida',
  'Gemelos Digitales',
  'Visualización Avanzada',
  'Otros'
];

// Define which fields are visible for each stage
const getVisibleFieldsForStage = (stage) => {
  const baseFields = ['title', 'company_id', 'description', 'estimated_value', 'expected_delivery_date', 'deadline_time',
    'link_pliego', 'link_infografia', 'link_pdf_resumen', 'link_notebooklm'];
  
  // Propuesta: adds SharePoint link, comments, and relevance
  const propuestaFields = [...baseFields, 'link_sharepoint', 'comments', 'relevance', 'relevance_comment'];
  
  // Presentada: adds offer details, probability, bidder info, and discount
  const presentadaFields = [...propuestaFields, 'offered_amount', 'tds_amount', 'delivery_date', 
    'probability', 'bidder', 'discount_percentage'];
  
  const resultFields = [...presentadaFields, 'final_awardee', 'award_amount', 'awardee_discount',
    'number_of_bidders', 'technical_position', 'economic_position', 'result_comments'];
  
  const descartadaFields = [...propuestaFields, 'discard_reason'];
  
  switch (stage) {
    case 'Detectada':
      return baseFields;
    case 'Propuesta':
      return propuestaFields;
    case 'Presentada':
      return presentadaFields;
    case 'Ganada':
    case 'Perdida':
      return resultFields;
    case 'Descartada':
      return descartadaFields;
    default:
      return baseFields;
  }
};

const OpportunityDialog = ({ open, onOpenChange, companyId, opportunity, onSuccess }) => {
  const { currentUser, editorUsers } = useUser();
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [typsaOrgUnits, setTypsaOrgUnits] = useState([]);
  const [stateChangeDialogOpen, setStateChangeDialogOpen] = useState(false);
  const [pendingStageChange, setPendingStageChange] = useState(null);
  
  // Client type selection state
  const [clientType, setClientType] = useState(''); // 'typsa', 'privada', 'publica'
  const [typsaCategory, setTypsaCategory] = useState(''); // 'Departamento', 'División', 'Territorio', 'Grupo'
  
  // Quick create dialogs state
  const [quickCompanyDialogOpen, setQuickCompanyDialogOpen] = useState(false);
  const [quickContactDialogOpen, setQuickContactDialogOpen] = useState(false);
  
  const initialFormData = {
    title: '',
    short_title: '',
    description: '',
    company_id: companyId || '',
    contact_id: '',
    stage: 'Detectada',
    owner: '',
    comments: '',
    typsa_org_unit_id: '',
    typsa_category: '',
    typsa_category_value: '',
    is_typsa_collaboration: false, // Cuando cliente TYPSA: true = Colab. TYPSA (proyecto en marcha), false = TYPSA (GG / I+D+i)
    prior_knowledge: null, // ¿Conocíamos la existencia previo a publicación?
    service_types: [], // Tipos de servicios involucrados
    other_service_type: '', // Descripción cuando se selecciona "Otros"
    // Detectada fields
    estimated_value: '',
    expected_delivery_date: '',
    deadline_time: '',
    // Duración estimada del contrato (valor + unidad). Obligatoria en Pública
    // desde Detectada; en Privada/TYPSA obligatoria al pasar a Presentada.
    contract_duration_value: '',
    contract_duration_unit: 'months',
    link_pliego: '',
    link_infografia: '',
    link_pdf_resumen: '',
    link_notebooklm: '',
    link_carpeta_trabajo: '',
    // Propuesta fields
    relevance: '',
    relevance_comment: '',
    link_sharepoint: '',
    discard_reason: '',
    // Feb 2026 (PR-B): discard classification + follow-up
    discard_reason_primary: '',
    discard_reason_secondary: '',
    expected_resolution_date: '',
    follow_up_active: false,
    create_followup_task: null,  // null => default (Pública=on, otros=off)
    // Presentada fields
    offered_amount: '',
    tds_amount: '',
    delivery_date: '',
    probability: '',
    bidder: '',
    discount_percentage: '',
    // Feb 2026 (matriz FTF): Modalidad de participación editable en el formulario
    // (antes solo se establecía en el StateChangeDialog).
    bid_modality: '', // 'solo' | 'ute' | 'subcontratista'
    // Matiz textual del estado (mostrado en Valoradas debajo del badge). Editable.
    stage_detail: '',
    // Responsable de seguimiento (Descartada). Editable también desde la ficha.
    follow_up_owner: '',
    // Ganada/Perdida fields
    final_awardee: '',
    award_amount: '',
    awardee_discount: '',
    number_of_bidders: '',
    global_position: '',
    technical_position: '',
    economic_position: '',
    result_comments: '',
    // Salto a producción (Ganada)
    tds_project_reference: '',
    tds_project_task: '',
    // Contract extensions (Ganada) — appended amounts summing to award_amount for the final contract value
    contract_extensions: [],
  };
  
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [generatingShortTitle, setGeneratingShortTitle] = useState(false);
  const [originalStage, setOriginalStage] = useState(null);

  // TYPSA categories - database stores lowercase versions
  const TYPSA_CATEGORIES = [
    { label: 'Departamento', value: 'departamento' },
    { label: 'División', value: 'division' },
    { label: 'Territorio', value: 'territorio' },
    { label: 'Grupo', value: 'grupo' }
  ];
  
  // Filter companies by type
  const privateCompanies = companies.filter(c => c.company_type === 'privada');
  const publicCompanies = companies.filter(c => c.company_type === 'publica');
  
  // Filter TYPSA units by category (compare with lowercase)
  const filteredTypsaUnits = typsaOrgUnits.filter(unit => 
    typsaCategory ? unit.category === typsaCategory : true
  );

  useEffect(() => {
    if (open) {
      fetchCompanies();
      fetchContacts();
      fetchTypsaOrgUnits();
    }
  }, [open]);

  useEffect(() => {
    if (opportunity) {
      const mappedData = {
        ...initialFormData,
        title: opportunity.title || opportunity.name || '',
        short_title: opportunity.short_title || '',
        description: opportunity.description || '',
        company_id: opportunity.company_id || companyId || '',
        contact_id: opportunity.contact_id || '',
        stage: opportunity.stage || 'Detectada',
        owner: opportunity.owner || '',
        comments: opportunity.comments || opportunity.notes || '',
        typsa_org_unit_id: opportunity.typsa_org_unit_id || '',
        typsa_category: opportunity.typsa_category || '',
        typsa_category_value: opportunity.typsa_category_value || '',
        is_typsa_collaboration: opportunity.is_typsa_collaboration === true,
        prior_knowledge: opportunity.prior_knowledge ?? null,
        service_types: opportunity.service_types || [],
        other_service_type: opportunity.other_service_type || '',
        estimated_value: opportunity.estimated_value || '',
        expected_delivery_date: opportunity.expected_delivery_date || opportunity.expected_close_date || '',
        deadline_time: opportunity.deadline_time || '',
        contract_duration_value: opportunity.contract_duration_value ?? '',
        contract_duration_unit: opportunity.contract_duration_unit || 'months',
        link_pliego: opportunity.link_pliego || '',
        link_infografia: opportunity.link_infografia || '',
        link_pdf_resumen: opportunity.link_pdf_resumen || '',
        link_notebooklm: opportunity.link_notebooklm || '',
        link_carpeta_trabajo: opportunity.link_carpeta_trabajo || '',
        relevance: opportunity.relevance || '',
        relevance_comment: opportunity.relevance_comment || '',
        link_sharepoint: opportunity.link_sharepoint || '',
        discard_reason: opportunity.discard_reason || '',
        discard_reason_primary: opportunity.discard_reason_primary || '',
        discard_reason_secondary: opportunity.discard_reason_secondary || '',
        expected_resolution_date: opportunity.expected_resolution_date || '',
        follow_up_active: opportunity.follow_up_active === true,
        create_followup_task: null,
        offered_amount: opportunity.offered_amount || '',
        tds_amount: opportunity.tds_amount || '',
        delivery_date: opportunity.delivery_date || '',
        probability: opportunity.probability || '',
        bidder: opportunity.bidder || '',
        discount_percentage: opportunity.discount_percentage || '',
        bid_modality: opportunity.bid_modality || '',
        stage_detail: opportunity.stage_detail || '',
        follow_up_owner: opportunity.follow_up_owner || '',
        final_awardee: opportunity.final_awardee || '',
        award_amount: opportunity.award_amount || '',
        awardee_discount: opportunity.awardee_discount || '',
        number_of_bidders: opportunity.number_of_bidders || '',
        global_position: opportunity.global_position || '',
        technical_position: opportunity.technical_position || '',
        economic_position: opportunity.economic_position || '',
        result_comments: opportunity.result_comments || '',
        tds_project_reference: opportunity.tds_project_reference || '',
        tds_project_task: opportunity.tds_project_task || '',
        contract_extensions: opportunity.contract_extensions || [],
      };
      setFormData(mappedData);
      setOriginalStage(opportunity.stage || 'Detectada');
      
      // Set client type based on existing data
      if (opportunity.typsa_org_unit_id) {
        setClientType('typsa');
        setTypsaCategory(opportunity.typsa_category || '');
      } else if (opportunity.company_id) {
        // Find the company to determine its type
        const company = companies.find(c => c.id === opportunity.company_id);
        if (company) {
          setClientType(company.company_type === 'privada' ? 'privada' : 'publica');
        }
      }
    } else {
      setFormData({
        ...initialFormData,
        company_id: companyId || '',
      });
      setOriginalStage(null);
      setClientType('');
      setTypsaCategory('');
    }
  }, [opportunity, companyId, open]);

  // Separate effect: detect clientType from companies when editing existing opportunity
  // (companies load asynchronously, this runs once companies are available)
  useEffect(() => {
    if (opportunity?.company_id && companies.length > 0 && !opportunity.typsa_org_unit_id && !clientType) {
      const company = companies.find(c => c.id === opportunity.company_id);
      if (company) {
        setClientType(company.company_type === 'privada' ? 'privada' : 'publica');
      }
    }
  }, [companies]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchTypsaOrgUnits = async () => {
    try {
      const response = await axios.get(`${API}/typsa-org-units`);
      setTypsaOrgUnits(response.data);
    } catch (error) {
      console.error('Error fetching TYPSA org units:', error);
    }
  };

  // Filter contacts by selected company OR TYPSA org unit
  const filteredContacts = formData.typsa_org_unit_id
    ? contacts.filter(c => c.typsa_org_unit_id === formData.typsa_org_unit_id)
    : formData.company_id
      ? contacts.filter(c => c.company_id === formData.company_id)
      : [];

  // Check if selected company is TYPSA
  const selectedCompany = companies.find(c => c.id === formData.company_id);
  const isTypsaCompany = selectedCompany?.name?.toUpperCase().includes('TYPSA') || selectedCompany?.company_type === 'typsa';

  // Category labels for display
  const CATEGORY_LABELS = {
    'division': 'División',
    'departamento': 'Departamento',
    'territorio': 'Territorio',
    'grupo': 'Grupo'
  };

  // Group TYPSA org units by category for display
  const getOrgUnitsByCategory = () => {
    const grouped = {};
    typsaOrgUnits.forEach(unit => {
      const cat = unit.category;
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(unit);
    });
    // Sort within each category
    Object.keys(grouped).forEach(cat => {
      grouped[cat].sort((a, b) => (a.value || '').localeCompare(b.value || '', 'es'));
    });
    return grouped;
  };

  const orgUnitsByCategory = getOrgUnitsByCategory();
  const selectedOrgUnit = typsaOrgUnits.find(u => u.id === formData.typsa_org_unit_id);

  // Handle contact change for TYPSA auto-fill
  const handleContactChange = (value) => {
    const newContactId = value === 'none' ? '' : value;
    let newTypsaOrgUnitId = formData.typsa_org_unit_id;
    let newTypsaCategory = '';
    let newTypsaCategoryValue = '';
    
    if (isTypsaCompany && newContactId) {
      const contact = contacts.find(c => c.id === newContactId);
      // Auto-fill from contact's org unit if contact has one
      if (contact?.typsa_org_unit_id) {
        newTypsaOrgUnitId = contact.typsa_org_unit_id;
        const orgUnit = typsaOrgUnits.find(u => u.id === contact.typsa_org_unit_id);
        if (orgUnit) {
          newTypsaCategory = orgUnit.category;
          newTypsaCategoryValue = orgUnit.value;
        }
      } else if (contact?.typsa_category && contact?.typsa_category_value) {
        // Legacy support
        newTypsaCategory = contact.typsa_category;
        newTypsaCategoryValue = contact.typsa_category_value;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      contact_id: newContactId,
      typsa_org_unit_id: newTypsaOrgUnitId,
      typsa_category: newTypsaCategory,
      typsa_category_value: newTypsaCategoryValue
    }));
  };

  // Handle company change - reset contact and TYPSA fields
  const handleCompanyChange = (value) => {
    setFormData(prev => ({
      ...prev,
      company_id: value === 'none' ? '' : value,
      contact_id: '',
      typsa_org_unit_id: '',
      typsa_category: '',
      typsa_category_value: ''
    }));
  };

  // Handle TYPSA org unit change
  const handleTypsaOrgUnitChange = (value) => {
    const orgUnitId = value === 'none' ? '' : value;
    const orgUnit = typsaOrgUnits.find(u => u.id === orgUnitId);
    setFormData(prev => ({
      ...prev,
      typsa_org_unit_id: orgUnitId,
      typsa_category: orgUnit?.category || '',
      typsa_category_value: orgUnit?.value || ''
    }));
  };

  // Handle quick company creation success
  const handleQuickCompanySuccess = (newCompany) => {
    // Add new company to the list
    setCompanies(prev => [...prev, newCompany]);
    // Select the new company
    setFormData(prev => ({ ...prev, company_id: newCompany.id }));
  };

  // Handle quick contact creation success
  const handleQuickContactSuccess = (newContact) => {
    // Add new contact to the list
    setContacts(prev => [...prev, newContact]);
    // Select the new contact
    setFormData(prev => ({ ...prev, contact_id: newContact.id }));
  };

  const handleStageChange = (newStage) => {
    // Iter 94: red de seguridad — bloquear transiciones prohibidas incluso si
    // llegan por un camino no filtrado (drag&drop futuro, PATCH manual, etc.).
    const forbidden = FORBIDDEN_TRANSITIONS[originalStage] || [];
    if (originalStage && newStage !== originalStage && forbidden.includes(newStage)) {
      toast.error(`No se puede pasar de "${originalStage}" a "${newStage}".`);
      return;
    }
    // If editing an existing opportunity and stage is changing, show confirmation dialog
    if (opportunity && originalStage && newStage !== originalStage) {
      setPendingStageChange(newStage);
      setStateChangeDialogOpen(true);
    } else {
      setFormData({ ...formData, stage: newStage });
    }
  };

  const handleStateChangeConfirm = (payload) => {
    // payload includes: owner, reason, discard_reason_primary/secondary,
    // expected_resolution_date, create_followup_task, follow_up_active,
    // and (Propuesta) relevance, relevance_comment, link_carpeta_trabajo,
    // link_sharepoint, estimated_value, expected_delivery_date, deadline_time, link_pliego.
    const nextStage = pendingStageChange;
    const patch = { stage: nextStage, state_change_reason: payload.reason };

    // Responsable — el diálogo siempre devuelve un valor explícito (keep o change)
    if (payload.owner !== undefined) patch.owner = payload.owner;

    // Descartada
    if (nextStage === 'Descartada') {
      patch.discard_reason = payload.reason || formData.discard_reason;
      if (payload.discard_reason_primary) patch.discard_reason_primary = payload.discard_reason_primary;
      if (payload.discard_reason_secondary) patch.discard_reason_secondary = payload.discard_reason_secondary;
    }

    // Propuesta — merge only fields that came back defined
    ['relevance', 'relevance_comment', 'link_carpeta_trabajo', 'link_sharepoint',
     'estimated_value', 'expected_delivery_date', 'deadline_time', 'link_pliego',
     'bid_modality', 'offered_amount', 'tds_amount', 'delivery_date', 'bidder', 'discount_percentage',
     'award_amount', 'awardee_discount', 'number_of_bidders', 'final_awardee',
     'global_position', 'technical_position', 'economic_position',
     'result_comments', 'tds_project_reference', 'follow_up_owner']
      .forEach((k) => { if (payload[k] !== undefined) patch[k] = payload[k]; });

    // Descartada / Presentada
    if (payload.expected_resolution_date !== undefined && payload.expected_resolution_date !== null) {
      patch.expected_resolution_date = payload.expected_resolution_date;
    }
    if (payload.create_followup_task !== undefined && payload.create_followup_task !== null) {
      patch.create_followup_task = payload.create_followup_task;
    }
    if (payload.follow_up_active !== null && payload.follow_up_active !== undefined) {
      patch.follow_up_active = payload.follow_up_active;
    }

    setFormData({ ...formData, ...patch });
    setStateChangeDialogOpen(false);
    setPendingStageChange(null);
  };

  const handleStateChangeCancel = () => {
    setStateChangeDialogOpen(false);
    setPendingStageChange(null);
  };

  const generateShortTitle = async () => {
    if (!formData.title.trim()) {
      toast.error('Primero ingresa un título');
      return;
    }
    
    setGeneratingShortTitle(true);
    try {
      const response = await axios.post(`${API}/opportunities/generate-short-title`, {
        title: formData.title
      });
      setFormData(prev => ({ ...prev, short_title: response.data.short_title }));
      toast.success('Título corto generado');
    } catch (error) {
      console.error('Error generating short title:', error);
      toast.error('Error al generar el título corto');
    } finally {
      setGeneratingShortTitle(false);
    }
  };

  const validateForm = () => {
    const errors = [];
    const stage = formData.stage;

    // Título — siempre obligatorio
    if (!formData.title.trim()) errors.push('El título es obligatorio');

    // Título corto — obligatorio solo si el título supera los 30 caracteres
    if (formData.title.trim().length > 30 && !formData.short_title?.trim()) {
      errors.push('El título corto es obligatorio cuando el título supera los 30 caracteres');
    }

    // Cliente: TYPSA (unidad) o empresa (Privada/Pública)
    if (clientType === 'typsa') {
      if (!formData.typsa_org_unit_id) errors.push('La unidad TYPSA es obligatoria');
      if (formData.typsa_org_unit_id && formData.is_typsa_collaboration !== true && formData.is_typsa_collaboration !== false) {
        errors.push('Indica si es una colaboración en un proyecto en marcha');
      }
    } else if (clientType === 'privada' || clientType === 'publica') {
      if (!formData.company_id) errors.push(`La empresa ${clientType === 'privada' ? 'privada' : 'pública'} es obligatoria`);
      if (formData.prior_knowledge !== true && formData.prior_knowledge !== false) {
        errors.push('Indica si conocíamos la existencia de la oportunidad previo a su publicación');
      }
    } else {
      errors.push('El tipo de cliente es obligatorio');
    }

    // Responsable — obligatorio siempre
    if (!formData.owner) errors.push('El responsable es obligatorio');

    // Descripción — obligatoria siempre
    if (!formData.description?.trim()) errors.push('La descripción es obligatoria');

    // Identificación (estado Detectada+):
    //   - Todos los tipos: Valor Estimado obligatorio
    //   - Pública añade: Fecha Límite de Entrega, Hora Límite y Link a Pliego
    const estRaw = formData.estimated_value;
    // Matriz FTF Feb 2026: TYPSA Detectada relaja el Valor Estimado (OPT en Detectada).
    const estimatedRequired = !(clientType === 'typsa' && stage === 'Detectada');
    if (estimatedRequired && (estRaw === '' || estRaw === null || estRaw === undefined)) {
      errors.push(clientType === 'publica' ? 'El precio base de licitación es obligatorio' : 'El valor estimado es obligatorio');
    }
    if (clientType === 'publica') {
      if (!formData.expected_delivery_date) errors.push('La fecha límite de entrega es obligatoria (Pública)');
      if (!formData.deadline_time) errors.push('La hora límite de entrega es obligatoria (Pública)');
      if (!formData.link_pliego?.trim()) errors.push('El link al pliego es obligatorio (Pública)');
      // Duración: obligatoria desde Detectada en Pública.
      if (formData.contract_duration_value === '' || formData.contract_duration_value == null) {
        errors.push('La duración del contrato es obligatoria (Pública)');
      }
    }
    // Duración en Privada/TYPSA: solo obligatoria al pasar a Presentada+.
    if ((clientType === 'privada' || clientType === 'typsa') && ['Presentada', 'Ganada', 'Perdida'].includes(stage)) {
      if (formData.contract_duration_value === '' || formData.contract_duration_value == null) {
        errors.push('La duración del contrato es obligatoria al pasar a Presentada');
      }
    }

    // Tipos de servicios — obligatorio a partir de Propuesta
    if (stage !== 'Detectada' && (!formData.service_types || formData.service_types.length === 0)) {
      errors.push('Selecciona al menos un tipo de servicio involucrado');
    }

    // Propuesta+ — Detectada fields must be complete (crear directamente en Propuesta)
    if (stage !== 'Detectada') {
      if (formData.estimated_value === '' || formData.estimated_value == null) {
        errors.push(clientType === 'publica' ? 'El precio base de licitación es obligatorio' : 'El valor estimado es obligatorio');
      }
      if (!formData.expected_delivery_date) {
        errors.push('La fecha límite de entrega es obligatoria');
      }
      if (clientType === 'publica') {
        if (!formData.deadline_time) errors.push('La hora límite de entrega es obligatoria');
        if (!formData.link_pliego?.trim()) errors.push('El link al pliego es obligatorio');
      }
      // Propuesta requires: Relevancia + comentario + link carpeta + comentario de cambio.
      // Relevancia obligatoria en Propuesta/Presentada/Ganada/Perdida; opcional en Descartada.
      if (stage !== 'Descartada') {
        if (!formData.relevance) errors.push('La relevancia es obligatoria a partir de Propuesta');
        if (!formData.relevance_comment?.trim()) errors.push('El comentario de relevancia es obligatorio a partir de Propuesta');
      }
      // Link Carpeta: obligatorio en Propuesta/Presentada/Ganada/Perdida; opcional en Descartada.
      if (stage !== 'Descartada' && !formData.link_carpeta_trabajo?.trim()) {
        errors.push('El link a la carpeta de trabajo inicial es obligatorio a partir de Propuesta');
      }
      // Matriz FTF: Contacto principal obligatorio en Priv/TYPSA a partir de Propuesta.
      if ((clientType === 'privada' || clientType === 'typsa') && !formData.contact_id) {
        errors.push('El contacto principal es obligatorio a partir de Propuesta');
      }
    }

    // Validate non-negative numbers
    if (formData.estimated_value && parseFloat(formData.estimated_value) < 0) {
      errors.push('El valor estimado no puede ser negativo');
    }
    if (formData.offered_amount && parseFloat(formData.offered_amount) < 0) {
      errors.push('El importe ofertado no puede ser negativo');
    }
    if (formData.discount_percentage && (parseFloat(formData.discount_percentage) < 0 || parseFloat(formData.discount_percentage) > 100)) {
      errors.push('El porcentaje de bajada debe estar entre 0 y 100');
    }

    // Presentada requires probability
    if (['Presentada', 'Ganada', 'Perdida'].includes(stage)) {
      if (!formData.probability) errors.push('Las probabilidades son obligatorias en estado "Presentada"');
      // Matriz FTF: en Presentada+, los siguientes son obligatorios (todos los scopes):
      if (formData.offered_amount === '' || formData.offered_amount == null) errors.push('El importe ofertado es obligatorio en Presentada');
      if (!formData.delivery_date) errors.push('La fecha de entrega real es obligatoria en Presentada');
      // Link SharePoint obligatorio en Presentada+ solo para Pública/Privada (TYPSA no aplica).
      if (clientType !== 'typsa' && !formData.link_sharepoint?.trim()) {
        errors.push('El enlace SharePoint de la oferta es obligatorio en Presentada');
      }
      // Modalidad y bidder obligatorios solo en Pública/Privada (TYPSA se auto-rellena).
      if (clientType !== 'typsa') {
        if (!formData.bid_modality) errors.push('La modalidad de participación es obligatoria en Presentada');
        // Si es UTE o Subcontratista → importe TDS obligatorio.
        if (['ute', 'subcontratista'].includes(formData.bid_modality)) {
          if (formData.tds_amount === '' || formData.tds_amount == null) {
            errors.push('El importe para TDS es obligatorio en modalidad UTE/Subcontratista');
          }
        }
        // Bidder solo obligatorio si NO es Individual (en Individual se auto-rellena a "TDS").
        if (formData.bid_modality !== 'solo' && !formData.bidder?.trim()) {
          errors.push('El licitador es obligatorio en Presentada');
        }
      }
    }

    // Descartada requires only the reason (comentario) — iter 92: primary/secondary eliminados.
    if (stage === 'Descartada') {
      if (!formData.discard_reason?.trim()) {
        errors.push('El comentario del descarte es obligatorio');
      }
    }

    // Feb 2026 (A6): PÚBLICA offer > presupuesto base => exclusión.
    if (clientType === 'publica') {
      const est = parseFloat(formData.estimated_value);
      const off = parseFloat(formData.offered_amount);
      if (!Number.isNaN(est) && !Number.isNaN(off) && est > 0 && off > est) {
        const ok = typeof window !== 'undefined' && window.confirm(
          'La oferta supera el presupuesto base de licitación. En licitación pública esto implica exclusión. ¿Confirmas el importe?'
        );
        if (!ok) errors.push('Cancelado: la oferta supera el presupuesto base.');
      }
    }

    // Ganada / Perdida — iter 91: reglas por scope
    if (stage === 'Ganada') {
      if (!formData.result_comments?.trim()) errors.push('El comentario del resultado es obligatorio');
      if (clientType === 'publica') {
        // award_amount es auto (= offered_amount), no se exige.
        if (formData.number_of_bidders === '' || formData.number_of_bidders === null) errors.push('El número de licitadores es obligatorio (Pública)');
        if (formData.technical_position === '' || formData.technical_position === null) errors.push('La posición técnica es obligatoria (Pública)');
        if (formData.economic_position === '' || formData.economic_position === null) errors.push('La posición económica es obligatoria (Pública)');
      }
      // Privada y TYPSA: solo comentario obligatorio.
    }
    if (stage === 'Perdida') {
      if (!formData.result_comments?.trim()) errors.push('El comentario del resultado es obligatorio');
      if (clientType === 'publica') {
        if (!formData.final_awardee?.trim()) errors.push('El adjudicatario final es obligatorio (Pública)');
        if (formData.award_amount === '' || formData.award_amount === null) errors.push('El importe de adjudicación es obligatorio (Pública)');
        if (formData.awardee_discount === '' || formData.awardee_discount === null) errors.push('La baja de la empresa adjudicataria es obligatoria (Pública)');
        if (formData.number_of_bidders === '' || formData.number_of_bidders === null) errors.push('El número de licitadores es obligatorio (Pública)');
        if (formData.global_position === '' || formData.global_position === null) errors.push('La posición global es obligatoria (Pública)');
        if (formData.technical_position === '' || formData.technical_position === null) errors.push('La posición técnica es obligatoria (Pública)');
        if (formData.economic_position === '' || formData.economic_position === null) errors.push('La posición económica es obligatoria (Pública)');
      }
      // Privada y TYPSA: solo comentario obligatorio.
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (errors.length > 0) {
      errors.forEach(err => toast.error(err));
      return;
    }

    const payload = {
      title: formData.title,
      short_title: formData.short_title || null,
      description: formData.description || null,
      company_id: formData.company_id || null,
      stage: formData.stage,
      owner: formData.owner || null,
      comments: formData.comments || null,
      contact_id: formData.contact_id || null,
      typsa_org_unit_id: clientType === 'typsa' ? (formData.typsa_org_unit_id || null) : null,
      typsa_category: formData.typsa_category || null,
      typsa_category_value: formData.typsa_category_value || null,
      is_typsa_collaboration: clientType === 'typsa' ? !!formData.is_typsa_collaboration : false,
      prior_knowledge: (clientType === 'privada' || clientType === 'publica') ? formData.prior_knowledge : null,
      service_types: formData.service_types && formData.service_types.length > 0 ? formData.service_types : null,
      other_service_type: formData.service_types?.includes('Otros') ? (formData.other_service_type || null) : null,
      estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null,
      expected_delivery_date: formData.expected_delivery_date || null,
      deadline_time: formData.deadline_time || null,
      contract_duration_value: formData.contract_duration_value !== '' && formData.contract_duration_value != null
        ? parseFloat(formData.contract_duration_value)
        : null,
      contract_duration_unit: (formData.contract_duration_value !== '' && formData.contract_duration_value != null)
        ? (formData.contract_duration_unit || 'months')
        : null,
      link_pliego: formData.link_pliego || null,
      // Feb 2026: link_infografia, link_pdf_resumen y link_notebooklm eliminados del formulario
      link_carpeta_trabajo: formData.link_carpeta_trabajo || null,
      relevance: formData.relevance || null,
      relevance_comment: formData.relevance_comment || null,
      link_sharepoint: formData.link_sharepoint || null,
      discard_reason: formData.discard_reason || null,
      discard_reason_primary: formData.discard_reason_primary || null,
      discard_reason_secondary: formData.discard_reason_secondary || null,
      expected_resolution_date: formData.expected_resolution_date || null,
      follow_up_active: (clientType === 'privada' || clientType === 'typsa')
        ? !!formData.follow_up_active
        : null,
      create_followup_task: formData.create_followup_task,
      offered_amount: formData.offered_amount ? parseFloat(formData.offered_amount) : null,
      // Matriz FTF: TDS auto = ofertado si TYPSA o Individual; si UTE/Sub, valor manual.
      tds_amount: (() => {
        const off = formData.offered_amount ? parseFloat(formData.offered_amount) : null;
        if (clientType === 'typsa' || formData.bid_modality === 'solo') return off;
        return formData.tds_amount ? parseFloat(formData.tds_amount) : null;
      })(),
      delivery_date: formData.delivery_date || null,
      probability: formData.probability || null,
      // Matriz FTF: bidder auto = "TDS" si TYPSA o Individual.
      bidder: (clientType === 'typsa' || formData.bid_modality === 'solo')
        ? 'TDS'
        : (formData.bidder || null),
      // Matriz FTF: en TYPSA se autoasigna 'subcontratista'; en Púb/Priv se guarda lo elegido.
      bid_modality: (clientType === 'typsa')
        ? 'subcontratista'
        : (formData.bid_modality || null),
      // Matiz textual del estado — editable en la ficha.
      stage_detail: formData.stage_detail || null,
      discount_percentage: (() => {
        // Feb 2026 (A5): only Pública has an auto-computed discount. For other
        // scopes we do NOT store a value.
        if (clientType !== 'publica') return null;
        const est = parseFloat(formData.estimated_value);
        const off = parseFloat(formData.offered_amount);
        if (!est || Number.isNaN(est) || Number.isNaN(off)) return null;
        return Math.round(((est - off) / est) * 100 * 100) / 100;
      })(),
      final_awardee: (() => {
        // Matriz FTF: Ganada → auto = bidder o "TDS"; Perdida → manual.
        if (formData.stage === 'Ganada') {
          if (clientType === 'typsa' || formData.bid_modality === 'solo') return 'TDS';
          return formData.bidder || 'TDS';
        }
        return formData.final_awardee || null;
      })(),
      award_amount: (() => {
        // Matriz FTF: en Ganada (todos los scopes) award = importe ofertado (auto).
        if (formData.stage === 'Ganada') {
          return formData.offered_amount ? parseFloat(formData.offered_amount) : null;
        }
        return formData.award_amount ? parseFloat(formData.award_amount) : null;
      })(),
      awardee_discount: (() => {
        // Matriz FTF: Ganada Pública → auto = discount_percentage; otros manual.
        if (formData.stage === 'Ganada' && clientType === 'publica') {
          const est = parseFloat(formData.estimated_value);
          const off = parseFloat(formData.offered_amount);
          if (!est || Number.isNaN(est) || Number.isNaN(off)) return null;
          return Math.round(((est - off) / est) * 100 * 100) / 100;
        }
        return formData.awardee_discount ? parseFloat(formData.awardee_discount) : null;
      })(),
      number_of_bidders: formData.number_of_bidders ? parseInt(formData.number_of_bidders) : null,
      global_position: (() => {
        // Matriz FTF: Ganada Pública → auto = 1; otros manual.
        if (formData.stage === 'Ganada' && clientType === 'publica') return 1;
        return formData.global_position ? parseInt(formData.global_position) : null;
      })(),
      technical_position: formData.technical_position ? parseInt(formData.technical_position) : null,
      economic_position: formData.economic_position ? parseInt(formData.economic_position) : null,
      result_comments: formData.result_comments || null,
      state_change_reason: formData.state_change_reason || null,
      // Follow-up owner editable desde la ficha (matriz FTF Feb 2026).
      follow_up_owner: formData.follow_up_owner || null,
      // Salto a producción (solo para Ganada)
      tds_project_reference: formData.stage === 'Ganada' ? (formData.tds_project_reference || null) : null,
      tds_project_task: formData.stage === 'Ganada' ? (formData.tds_project_task || null) : null,
      // Ampliaciones de contrato (solo para Ganada)
      contract_extensions: formData.stage === 'Ganada'
        ? (formData.contract_extensions || []).filter(e => e && (parseFloat(e.amount) || 0) !== 0)
        : null,
    };

    setLoading(true);
    try {
      const headers = { 'X-User-Email': currentUser?.email };
      
      if (opportunity) {
        await axios.put(`${API}/opportunities/${opportunity.id}`, payload, { headers });
        toast.success('Oportunidad actualizada correctamente');
      } else {
        await axios.post(`${API}/opportunities`, payload, { headers });
        toast.success('Oportunidad creada correctamente');
      }
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving opportunity:', error);
      const errorMsg = error.response?.data?.detail || 'Error al guardar la oportunidad';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const visibleFields = getVisibleFieldsForStage(formData.stage);
  const isFieldVisible = (field) => visibleFields.includes(field);
  const isResultStage = formData.stage === 'Ganada' || formData.stage === 'Perdida';
  const isDescartada = formData.stage === 'Descartada';

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0" data-testid="opportunity-dialog">
          <DialogHeader className="shrink-0 p-6 pb-0">
            <DialogTitle>{opportunity ? 'Editar Oportunidad' : 'Nueva Oportunidad'}</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto p-6 pt-4">
            {/* Aviso persistente: campos pendientes (Presentada/Ganada/Perdida sin SharePoint) — no aplica a TYPSA ni a Descartada */}
            {opportunity && ['Presentada', 'Ganada', 'Perdida'].includes(formData.stage) && clientType !== 'typsa' && !formData.link_sharepoint && (
              <div
                className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm"
                data-testid="sharepoint-pending-banner"
              >
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-amber-800">Falta el Link SharePoint de la oferta</p>
                  <p className="text-xs text-amber-700">Esta oportunidad quedará marcada como pendiente hasta que rellenes el enlace en la sección "Preparación de Propuesta".</p>
                </div>
              </div>
            )}
            {/* Aviso persistente: falta Proyecto TDS de referencia (solo en Ganada) */}
            {opportunity && formData.stage === 'Ganada' && !formData.tds_project_reference && (
              <div
                className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm"
                data-testid="tds-project-pending-banner"
              >
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-amber-800">Falta el Proyecto TDS de referencia</p>
                  <p className="text-xs text-amber-700">Esta oportunidad quedará marcada como pendiente hasta que rellenes la referencia en la sección "Salto a Producción".</p>
                </div>
              </div>
            )}
            {/* Aviso persistente: Descartada Pública con seguimiento activo sin resultado completo */}
            {opportunity && formData.stage === 'Descartada' && formData.follow_up_active && clientType === 'publica' && (
              !formData.final_awardee || !formData.award_amount || formData.awardee_discount == null ||
              !formData.number_of_bidders || !formData.global_position || !formData.technical_position || !formData.economic_position
            ) && (
              <div
                className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm"
                data-testid="descartada-followup-pending-banner"
              >
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-amber-800">Seguimiento pendiente de resultado</p>
                  <p className="text-xs text-amber-700">
                    {formData.follow_up_owner
                      ? <>Responsable: <strong>{formData.follow_up_owner}</strong>. </>
                      : null}
                    Faltan datos del resultado (adjudicatario, importe, baja, nº licitadores, posiciones). Rellénalos para cerrar el seguimiento.
                  </p>
                </div>
              </div>
            )}
            {/* Aviso persistente: Descartada con follow_up_owner sin follow_up_active (edge case) */}
            <form onSubmit={handleSubmit} noValidate className="space-y-6">
              {/* General Fields */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Información General</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="title">Título *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      data-testid="opportunity-title-input"
                      placeholder="Nombre de la oportunidad"
                    />
                    <DuplicateWarning
                      entityType="opportunity"
                      query={{
                        title: formData.title,
                        company_id: formData.company_id,
                        typsa_org_unit_id: formData.typsa_org_unit_id,
                      }}
                      excludeId={opportunity?.id}
                    />
                  </div>
                  
                  {/* Título Corto: solo obligatorio y visible si el título supera 30 caracteres */}
                  {formData.title.length > 30 && (
                    <div className="md:col-span-2">
                      <Label htmlFor="short_title">Título Corto *</Label>
                      <div className="flex gap-2">
                        <Input
                          id="short_title"
                          value={formData.short_title}
                          onChange={(e) => setFormData({ ...formData, short_title: e.target.value })}
                          data-testid="opportunity-short-title-input"
                          placeholder="Título corto para mostrar en tarjetas"
                          className="flex-1"
                          maxLength={60}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={generateShortTitle}
                          disabled={generatingShortTitle || !formData.title.trim()}
                          className="whitespace-nowrap"
                          data-testid="generate-short-title-btn"
                        >
                          {generatingShortTitle ? (
                            <span className="flex items-center gap-1">
                              <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              Generando...
                            </span>
                          ) : (
                            '✨ Generar con IA'
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Obligatorio porque el título supera los 30 caracteres. Se mostrará en las tarjetas del pipeline. Máximo 60 caracteres.
                      </p>
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="clientType">Tipo de Cliente *</Label>
                    <Select
                      value={clientType || "none"}
                      onValueChange={(value) => {
                        setClientType(value === 'none' ? '' : value);
                        setTypsaCategory('');
                        setFormData(prev => ({ 
                          ...prev, 
                          company_id: '', 
                          typsa_org_unit_id: '',
                          typsa_category: '',
                          typsa_category_value: ''
                        }));
                      }}
                    >
                      <SelectTrigger data-testid="opportunity-client-type-select">
                        <SelectValue placeholder="Seleccionar tipo de cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Seleccionar tipo...</SelectItem>
                        <SelectItem value="typsa">TYPSA</SelectItem>
                        <SelectItem value="privada">Privada</SelectItem>
                        <SelectItem value="publica">Pública</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* TYPSA: Category selector */}
                  {clientType === 'typsa' && (
                    <div>
                      <Label htmlFor="typsaCategory">Categoría TYPSA *</Label>
                      <Select
                        value={typsaCategory || "none"}
                        onValueChange={(value) => {
                          setTypsaCategory(value === 'none' ? '' : value);
                          setFormData(prev => ({ 
                            ...prev, 
                            typsa_org_unit_id: '',
                            typsa_category: value === 'none' ? '' : value,
                            typsa_category_value: ''
                          }));
                        }}
                      >
                        <SelectTrigger data-testid="opportunity-typsa-category-select">
                          <SelectValue placeholder="Seleccionar categoría" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Seleccionar categoría...</SelectItem>
                          {TYPSA_CATEGORIES.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* TYPSA: Unit selector based on category */}
                  {clientType === 'typsa' && typsaCategory && (
                    <div className="md:col-span-2">
                      <Label htmlFor="typsa_org_unit">
                        {TYPSA_CATEGORIES.find(c => c.value === typsaCategory)?.label || typsaCategory} *
                      </Label>
                      <Select
                        value={formData.typsa_org_unit_id || "none"}
                        onValueChange={(value) => {
                          const unit = typsaOrgUnits.find(u => u.id === value);
                          setFormData(prev => ({ 
                            ...prev, 
                            typsa_org_unit_id: value === 'none' ? '' : value,
                            typsa_category: unit?.category || typsaCategory,
                            typsa_category_value: unit?.value || ''
                          }));
                        }}
                      >
                        <SelectTrigger data-testid="opportunity-typsa-unit-select">
                          <SelectValue placeholder={`Seleccionar ${TYPSA_CATEGORIES.find(c => c.value === typsaCategory)?.label?.toLowerCase() || typsaCategory}`} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Seleccionar...</SelectItem>
                          {filteredTypsaUnits.map((unit) => (
                            <SelectItem key={unit.id} value={unit.id}>
                              {unit.value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* TYPSA: Colaboración en proyectos en marcha? */}
                  {clientType === 'typsa' && formData.typsa_org_unit_id && (
                    <div className="md:col-span-2">
                      <Label>¿Es una colaboración en un proyecto en marcha? *</Label>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="is_typsa_collaboration"
                            checked={formData.is_typsa_collaboration === true}
                            onChange={() => setFormData(prev => ({ ...prev, is_typsa_collaboration: true }))}
                            className="w-4 h-4 text-primary"
                            data-testid="typsa-collab-yes"
                          />
                          <span>Sí <span className="text-xs text-muted-foreground">(Colaboración TYPSA)</span></span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="is_typsa_collaboration"
                            checked={formData.is_typsa_collaboration === false}
                            onChange={() => setFormData(prev => ({ ...prev, is_typsa_collaboration: false }))}
                            className="w-4 h-4 text-primary"
                            data-testid="typsa-collab-no"
                          />
                          <span>No <span className="text-xs text-muted-foreground">(TYPSA: GG / I+D+i)</span></span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Private/Public: Company selector */}
                  {(clientType === 'privada' || clientType === 'publica') && (
                    <div className="md:col-span-2">
                      <Label htmlFor="company">
                        Empresa {clientType === 'privada' ? 'Privada' : 'Pública'} *
                      </Label>
                      <div className="flex gap-2">
                        <Select
                          value={formData.company_id || "none"}
                          onValueChange={handleCompanyChange}
                        >
                          <SelectTrigger data-testid="opportunity-company-select" className="flex-1">
                            <SelectValue placeholder="Seleccionar empresa" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Seleccionar empresa...</SelectItem>
                            {(clientType === 'privada' ? privateCompanies : publicCompanies).map((company) => (
                              <SelectItem key={company.id} value={company.id}>
                                {company.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setQuickCompanyDialogOpen(true)}
                          title={`Crear nueva empresa ${clientType === 'privada' ? 'privada' : 'pública'}`}
                          data-testid="create-company-btn"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Prior knowledge question - only for Privada/Pública */}
                  {(clientType === 'privada' || clientType === 'publica') && (
                    <div className="md:col-span-2">
                      <Label>¿Conocíamos la existencia de esta oportunidad previo a su publicación? *</Label>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="prior_knowledge"
                            checked={formData.prior_knowledge === true}
                            onChange={() => setFormData(prev => ({ ...prev, prior_knowledge: true }))}
                            className="w-4 h-4 text-primary"
                            data-testid="prior-knowledge-yes"
                          />
                          <span>Sí</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="prior_knowledge"
                            checked={formData.prior_knowledge === false}
                            onChange={() => setFormData(prev => ({ ...prev, prior_knowledge: false }))}
                            className="w-4 h-4 text-primary"
                            data-testid="prior-knowledge-no"
                          />
                          <span>No</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Contact selector - for non-TYPSA companies OR TYPSA with selected org unit */}
                  {((formData.company_id && clientType !== 'typsa') || (clientType === 'typsa' && formData.typsa_org_unit_id)) && (
                    <div>
                      <Label htmlFor="contact">Contacto principal</Label>
                      <div className="flex gap-2">
                        <Select
                          value={formData.contact_id || "none"}
                          onValueChange={handleContactChange}
                        >
                          <SelectTrigger data-testid="opportunity-contact-select" className="flex-1">
                            <SelectValue placeholder="Seleccionar contacto" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Ninguno</SelectItem>
                            {filteredContacts.map((contact) => (
                              <SelectItem key={contact.id} value={contact.id}>
                                {contact.first_name || contact.name} {contact.last_name || ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setQuickContactDialogOpen(true)}
                          title="Crear nuevo contacto"
                          data-testid="create-contact-btn"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="stage">Estado *</Label>
                    <Select 
                      value={formData.stage} 
                      onValueChange={handleStageChange}
                    >
                      <SelectTrigger data-testid="opportunity-stage-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {getAllowedStages(originalStage || formData.stage).map((stage) => (
                          <SelectItem key={stage} value={stage}>
                            {stage}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Matiz del estado (stage_detail) — texto libre editable, se muestra en Valoradas. */}
                  <div className="md:col-span-2">
                    <Label htmlFor="stage_detail" className="flex items-center gap-1">
                      Matiz del estado
                      <span
                        className="inline-flex items-center text-muted-foreground cursor-help"
                        title="Lo que escribas aquí aparece en la pestaña de Valoradas (Excel para Miguel), debajo del estado."
                        data-testid="stage-detail-info"
                      >
                        <Info className="w-3.5 h-3.5" />
                      </span>
                    </Label>
                    <Textarea
                      id="stage_detail"
                      value={formData.stage_detail}
                      onChange={(e) => setFormData({ ...formData, stage_detail: e.target.value })}
                      onInput={(e) => {
                        // Auto-grow: reset y ajustar a scrollHeight en cada cambio.
                        e.target.style.height = 'auto';
                        e.target.style.height = `${e.target.scrollHeight}px`;
                      }}
                      placeholder="Detalle (opcional). El cuadro se ampliará automáticamente."
                      rows={1}
                      className="resize-none overflow-hidden min-h-[38px]"
                      data-testid="opportunity-stage-detail"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="owner">Responsable *</Label>
                    <Select
                      value={formData.owner || "none"}
                      onValueChange={(value) => setFormData({ ...formData, owner: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="opportunity-owner-select">
                        <SelectValue placeholder="Seleccionar responsable" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin asignar</SelectItem>
                        {editorUsers?.map((user) => (
                          <SelectItem key={user.email} value={user.name}>
                            {user.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Tipos de Servicios - Selección múltiple (obligatorio a partir de Propuesta) */}
                  <div className="md:col-span-2">
                    <Label>Tipos de Servicios Involucrados{formData.stage !== 'Detectada' ? ' *' : ''}</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 p-3 border rounded-md bg-muted/30">
                      {SERVICE_TYPES.map((service) => (
                        <label key={service} className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 p-1.5 rounded">
                          <input
                            type="checkbox"
                            checked={formData.service_types?.includes(service) || false}
                            onChange={(e) => {
                              const currentServices = formData.service_types || [];
                              if (e.target.checked) {
                                setFormData(prev => ({
                                  ...prev,
                                  service_types: [...currentServices, service]
                                }));
                              } else {
                                setFormData(prev => ({
                                  ...prev,
                                  service_types: currentServices.filter(s => s !== service),
                                  // Clear other_service_type if "Otros" is unchecked
                                  ...(service === 'Otros' ? { other_service_type: '' } : {})
                                }));
                              }
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                            data-testid={`service-type-${service.toLowerCase().replace(/\s+/g, '-')}`}
                          />
                          <span className="text-sm">{service}</span>
                        </label>
                      ))}
                    </div>
                    
                    {/* Campo de texto para "Otros" */}
                    {formData.service_types?.includes('Otros') && (
                      <div className="mt-2">
                        <Input
                          placeholder="Especifica el tipo de servicio..."
                          value={formData.other_service_type || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, other_service_type: e.target.value }))}
                          maxLength={100}
                          data-testid="other-service-type-input"
                        />
                      </div>
                    )}
                    
                    <p className="text-xs text-muted-foreground mt-1">
                      Selecciona todos los tipos de servicios que aplican a esta oportunidad.
                    </p>
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="description">Descripción *</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                      placeholder="Descripción de la oportunidad"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Fase Detectada - Identificación: campos condicionados por tipo de cliente */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Identificación</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Valor Estimado — obligatorio para todos los tipos de cliente */}
                  <div>
                    <Label htmlFor="estimated_value">
                      {clientType === 'publica' ? 'Precio Base de Licitación (€) *' : 'Valor Estimado (€) *'}
                    </Label>
                    <Input
                      id="estimated_value"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.estimated_value}
                      onChange={(e) => setFormData({ ...formData, estimated_value: e.target.value })}
                      data-testid="opportunity-value-input"
                      placeholder="0.00"
                    />
                  </div>

                  {/* Fecha Límite de Entrega — obligatoria SOLO en Pública */}
                  <div>
                    <Label htmlFor="expected_delivery_date">
                      Fecha Límite de Entrega{clientType === 'publica' ? ' *' : ''}
                    </Label>
                    <Input
                      id="expected_delivery_date"
                      type="date"
                      value={formData.expected_delivery_date}
                      onChange={(e) => setFormData({ ...formData, expected_delivery_date: e.target.value })}
                      data-testid="opportunity-date-input"
                    />
                  </div>

                  {/* Duración del contrato — obligatoria en Pública desde Detectada;
                      en Privada/TYPSA visible desde Detectada, obligatoria en Presentada+. */}
                  <div className="md:col-span-2">
                    <Label htmlFor="contract_duration_value">
                      Duración
                      {clientType === 'publica' && ' *'}
                      {(clientType === 'privada' || clientType === 'typsa') &&
                        ['Presentada', 'Ganada', 'Perdida'].includes(formData.stage) && ' *'}
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        id="contract_duration_value"
                        type="number"
                        step="1"
                        min="0"
                        value={formData.contract_duration_value}
                        onChange={(e) => setFormData({ ...formData, contract_duration_value: e.target.value })}
                        placeholder="0"
                        data-testid="opportunity-duration-value"
                      />
                      <Select
                        value={formData.contract_duration_unit || 'months'}
                        onValueChange={(v) => setFormData({ ...formData, contract_duration_unit: v })}
                      >
                        <SelectTrigger data-testid="opportunity-duration-unit">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="days" data-testid="duration-unit-days">Días</SelectItem>
                          <SelectItem value="weeks" data-testid="duration-unit-weeks">Semanas</SelectItem>
                          <SelectItem value="months" data-testid="duration-unit-months">Meses</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {(clientType === 'privada' || clientType === 'typsa') &&
                      !['Presentada', 'Ganada', 'Perdida'].includes(formData.stage) && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Recomendado desde Detectada. Obligatorio al pasar a Presentada.
                        </p>
                      )}
                  </div>

                  {/* Link Carpeta de trabajo inicial — visible siempre.
                      Obligatorio en Propuesta/Presentada/Ganada/Perdida; opcional en Detectada y Descartada. */}
                  <div className="md:col-span-2">
                    <Label htmlFor="link_carpeta_trabajo">
                      Link Carpeta de trabajo inicial
                      {['Propuesta', 'Presentada', 'Ganada', 'Perdida'].includes(formData.stage) && ' *'}
                    </Label>
                    <Input
                      id="link_carpeta_trabajo"
                      type="url"
                      value={formData.link_carpeta_trabajo}
                      onChange={(e) => setFormData({ ...formData, link_carpeta_trabajo: e.target.value })}
                      placeholder="https://..."
                      data-testid="opportunity-link-carpeta-trabajo"
                    />
                  </div>

                  {/* Los siguientes campos SOLO se muestran cuando el cliente es Pública */}
                  {clientType === 'publica' && (
                    <>
                      <div>
                        <Label htmlFor="deadline_time">Hora Límite de Entrega *</Label>
                        <Input
                          id="deadline_time"
                          type="time"
                          value={formData.deadline_time}
                          onChange={(e) => setFormData({ ...formData, deadline_time: e.target.value })}
                          data-testid="opportunity-time-input"
                        />
                      </div>

                      <div>
                        <Label htmlFor="link_pliego" className="flex items-center gap-1">
                          Link a Pliego *
                          <span
                            className="inline-flex items-center text-muted-foreground cursor-help"
                            title="Se recomienda copiar aquí el enlace del expediente en TYPSA Deal."
                            data-testid="link-pliego-info"
                          >
                            <Info className="w-3.5 h-3.5" />
                          </span>
                        </Label>
                        <Input
                          id="link_pliego"
                          type="url"
                          value={formData.link_pliego}
                          onChange={(e) => setFormData({ ...formData, link_pliego: e.target.value })}
                          placeholder="https://..."
                          data-testid="opportunity-link-pliego"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Se recomienda usar el enlace del expediente en TYPSA Deal.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Fase Propuesta - Visible from Propuesta onwards */}
              {['Propuesta', 'Presentada', 'Ganada', 'Perdida', 'Descartada'].includes(formData.stage) && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Preparación de Propuesta</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="relevance">
                          Relevancia
                          {['Propuesta', 'Presentada', 'Ganada', 'Perdida'].includes(formData.stage) && ' *'}
                        </Label>
                        <Select
                          value={formData.relevance || "none"}
                          onValueChange={(value) => setFormData({ ...formData, relevance: value === "none" ? "" : value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin especificar</SelectItem>
                            {RELEVANCE_OPTIONS.map((opt) => (
                              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label htmlFor="relevance_comment">
                          Comentario de Relevancia
                          {['Propuesta', 'Presentada', 'Ganada', 'Perdida'].includes(formData.stage) && ' *'}
                        </Label>
                        <Input
                          id="relevance_comment"
                          value={formData.relevance_comment}
                          onChange={(e) => setFormData({ ...formData, relevance_comment: e.target.value })}
                          placeholder="Motivo de la relevancia asignada"
                        />
                      </div>
                      
                      {/* Enlace SharePoint — NUNCA aparece en TYPSA (no aplica) */}
                      {clientType !== 'typsa' && (
                        <div className="md:col-span-2">
                          <Label htmlFor="link_sharepoint">
                            Enlace a SharePoint de la Oferta
                            {['Presentada', 'Ganada', 'Perdida'].includes(formData.stage) && ' *'}
                          </Label>
                          <Input
                            id="link_sharepoint"
                            type="url"
                            value={formData.link_sharepoint}
                            onChange={(e) => setFormData({ ...formData, link_sharepoint: e.target.value })}
                            placeholder="https://..."
                          />
                        </div>
                      )}
                      
                      <div className="md:col-span-2">
                        <Label htmlFor="comments">Comentarios</Label>
                        <Textarea
                          id="comments"
                          value={formData.comments}
                          onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                          rows={3}
                          placeholder="Comentarios adicionales"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Fase Presentada - Visible from Presentada onwards */}
              {['Presentada', 'Ganada', 'Perdida'].includes(formData.stage) && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Presentación de Oferta</h3>

                    {/* Modalidad de participación — Pública/Privada. En TYPSA se autoasigna Subcontratista. */}
                    {clientType !== 'typsa' && (
                      <div>
                        <Label htmlFor="bid_modality">
                          Modalidad de participación *
                          <span
                            className="inline-flex items-center text-muted-foreground ml-1 cursor-help"
                            title="Si vamos Individual, el importe ofertado es solo de TDS. Si vamos en UTE o como Subcontratistas, se rellena aparte el importe para TDS."
                          >
                            <Info className="w-3.5 h-3.5" />
                          </span>
                        </Label>
                        <Select
                          value={formData.bid_modality || ''}
                          onValueChange={(v) => setFormData({ ...formData, bid_modality: v })}
                        >
                          <SelectTrigger data-testid="opportunity-bid-modality">
                            <SelectValue placeholder="Seleccionar modalidad" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="solo" data-testid="bid-modality-solo">Individual</SelectItem>
                            <SelectItem value="ute" data-testid="bid-modality-ute">UTE</SelectItem>
                            <SelectItem value="subcontratista" data-testid="bid-modality-sub">Subcontratista</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="probability">Probabilidades *</Label>
                        <Select
                          value={formData.probability || "none"}
                          onValueChange={(value) => setFormData({ ...formData, probability: value === "none" ? "" : value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin especificar</SelectItem>
                            {PROBABILITY_OPTIONS.map((opt) => (
                              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label htmlFor="offered_amount">Importe Ofertado (€) *</Label>
                        <Input
                          id="offered_amount"
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.offered_amount}
                          onChange={(e) => setFormData({ ...formData, offered_amount: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                      
                      {/* Importe TDS — visible SOLO si UTE/Subcontratista en Púb/Priv.
                          En Individual o TYPSA se autorrellena con el importe ofertado. */}
                      {(clientType === 'typsa' || formData.bid_modality === 'solo') ? (
                        <div>
                          <Label htmlFor="tds_amount">Importe TDS (€)</Label>
                          <Input
                            id="tds_amount"
                            type="text"
                            readOnly
                            value={(() => {
                              const off = parseFloat(formData.offered_amount);
                              return Number.isNaN(off) ? '' : `${off.toLocaleString('es-ES')} €`;
                            })()}
                            className="bg-muted/40 text-muted-foreground"
                            data-testid="tds-amount-auto"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            {clientType === 'typsa'
                              ? 'En TYPSA coincide con el importe ofertado.'
                              : 'En modalidad Individual coincide con el importe ofertado.'}
                          </p>
                        </div>
                      ) : (
                        <div>
                          <Label htmlFor="tds_amount">Importe TDS (€) {formData.bid_modality ? '*' : ''}</Label>
                          <Input
                            id="tds_amount"
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.tds_amount}
                            onChange={(e) => setFormData({ ...formData, tds_amount: e.target.value })}
                            placeholder="0.00"
                          />
                        </div>
                      )}
                      
                      <div>
                        <Label htmlFor="delivery_date">Fecha de Entrega Real *</Label>
                        <Input
                          id="delivery_date"
                          type="date"
                          value={formData.delivery_date}
                          onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                        />
                      </div>
                      
                      {/* Licitador — TYPSA e Individual auto = "TDS" (readonly) */}
                      {(clientType === 'typsa' || formData.bid_modality === 'solo') ? (
                        <div>
                          <Label htmlFor="bidder">Licitador</Label>
                          <Input
                            id="bidder"
                            type="text"
                            readOnly
                            value="TDS"
                            className="bg-muted/40 text-muted-foreground"
                            data-testid="bidder-auto"
                          />
                          <p className="text-xs text-muted-foreground mt-1">Se rellena automáticamente.</p>
                        </div>
                      ) : (
                        <div>
                          <Label htmlFor="bidder">Licitador {formData.bid_modality ? '*' : ''}</Label>
                          <Input
                            id="bidder"
                            value={formData.bidder}
                            onChange={(e) => setFormData({ ...formData, bidder: e.target.value })}
                            placeholder="Nombre del licitador"
                          />
                        </div>
                      )}
                      
                      <div>
                        <Label htmlFor="discount_percentage">
                          Porcentaje de Bajada (%)
                          {clientType === 'publica' && (
                            <span className="text-xs text-muted-foreground ml-1">(auto)</span>
                          )}
                        </Label>
                        {clientType === 'publica' ? (
                          // Auto-computed, non-editable in Pública per Feb 2026 spec (A5).
                          <Input
                            id="discount_percentage"
                            type="text"
                            readOnly
                            data-testid="discount-auto"
                            value={(() => {
                              const est = parseFloat(formData.estimated_value);
                              const off = parseFloat(formData.offered_amount);
                              if (!est || Number.isNaN(est) || Number.isNaN(off)) return '';
                              const pct = ((est - off) / est) * 100;
                              return `${(Math.round(pct * 100) / 100).toString().replace('.', ',')}%`;
                            })()}
                            className="bg-muted/40 text-muted-foreground"
                          />
                        ) : (
                          // Hidden for Privada/TYPSA (bajada requires presupuesto base).
                          <div className="text-xs text-muted-foreground italic py-2" data-testid="discount-not-applicable">
                            No aplica: sin presupuesto base publicado
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Resultado Final - Only for Ganada/Perdida */}
              {isResultStage && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                      Resultado Final {clientType !== 'typsa' && '(Campos Obligatorios)'}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Importe Adjudicación — auto = Importe Ofertado en Ganada (todos los scopes).
                          Solo se pide manualmente en Perdida. */}
                      {formData.stage === 'Ganada' ? (
                        <div>
                          <Label htmlFor="award_amount">Importe Adjudicación (€)</Label>
                          <Input
                            id="award_amount"
                            type="text"
                            readOnly
                            value={(() => {
                              const off = parseFloat(formData.offered_amount);
                              return Number.isNaN(off) ? '' : `${off.toLocaleString('es-ES')} €`;
                            })()}
                            className="bg-muted/40 text-muted-foreground"
                            data-testid="award-amount-auto"
                          />
                          <p className="text-xs text-muted-foreground mt-1">Coincide con el importe ofertado.</p>
                        </div>
                      ) : (
                        <div>
                          <Label htmlFor="award_amount">Importe Adjudicación (€) *</Label>
                          <Input
                            id="award_amount"
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.award_amount}
                            onChange={(e) => setFormData({ ...formData, award_amount: e.target.value })}
                            placeholder="0.00"
                            required={isResultStage}
                          />
                        </div>
                      )}
                      
                      {/* Following fields only shown for external clients (Privada/Pública) */}
                      {clientType !== 'typsa' && (
                        <>
                          {/* Adjudicatario Final — auto en Ganada (= bidder o "TDS"), manual en Perdida */}
                          {formData.stage === 'Ganada' ? (
                            <div>
                              <Label htmlFor="final_awardee">Adjudicatario Final</Label>
                              <Input
                                id="final_awardee"
                                type="text"
                                readOnly
                                value={(formData.bid_modality === 'solo') ? 'TDS' : (formData.bidder || 'TDS')}
                                className="bg-muted/40 text-muted-foreground"
                                data-testid="final-awardee-auto"
                              />
                              <p className="text-xs text-muted-foreground mt-1">En Ganada coincide con el licitador.</p>
                            </div>
                          ) : (
                            <div>
                              <Label htmlFor="final_awardee">Adjudicatario Final *</Label>
                              <Input
                                id="final_awardee"
                                value={formData.final_awardee}
                                onChange={(e) => setFormData({ ...formData, final_awardee: e.target.value })}
                                placeholder="Nombre del adjudicatario"
                                required={isResultStage && clientType !== 'typsa'}
                              />
                            </div>
                          )}
                          
                          {/* Baja Adjudicatario — Ganada Pública auto (= discount_percentage); Perdida manual */}
                          {(formData.stage === 'Ganada' && clientType === 'publica') ? (
                            <div>
                              <Label htmlFor="awardee_discount">Baja Adjudicatario (%)</Label>
                              <Input
                                id="awardee_discount"
                                type="text"
                                readOnly
                                value={(() => {
                                  const est = parseFloat(formData.estimated_value);
                                  const off = parseFloat(formData.offered_amount);
                                  if (!est || Number.isNaN(est) || Number.isNaN(off)) return '';
                                  const pct = ((est - off) / est) * 100;
                                  return `${(Math.round(pct * 100) / 100).toString().replace('.', ',')} %`;
                                })()}
                                className="bg-muted/40 text-muted-foreground"
                                data-testid="awardee-discount-auto"
                              />
                              <p className="text-xs text-muted-foreground mt-1">Auto = % de bajada calculada.</p>
                            </div>
                          ) : formData.stage === 'Ganada' && clientType === 'privada' ? null : (
                            <div>
                              <Label htmlFor="awardee_discount">Baja Adjudicatario (%) *</Label>
                              <Input
                                id="awardee_discount"
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                value={formData.awardee_discount}
                                onChange={(e) => setFormData({ ...formData, awardee_discount: e.target.value })}
                                placeholder="0.00"
                                required={isResultStage && clientType !== 'typsa'}
                              />
                            </div>
                          )}
                          
                          <div>
                            <Label htmlFor="number_of_bidders">
                              Número de Licitadores
                              {!(formData.stage === 'Ganada' && clientType === 'privada') && ' *'}
                            </Label>
                            <Input
                              id="number_of_bidders"
                              type="number"
                              min="1"
                              value={formData.number_of_bidders}
                              onChange={(e) => setFormData({ ...formData, number_of_bidders: e.target.value })}
                              placeholder="1"
                              required={isResultStage && clientType !== 'typsa' && !(formData.stage === 'Ganada' && clientType === 'privada')}
                            />
                          </div>
                          
                          {/* Posición Global / Técnica / Económica — no aparecen en Privada + Ganada. */}
                          {!(formData.stage === 'Ganada' && clientType === 'privada') && (
                            <>
                              {/* Posición Global — Ganada Pública auto = 1; Perdida manual */}
                              {(formData.stage === 'Ganada' && clientType === 'publica') ? (
                                <div>
                                  <Label htmlFor="global_position">Posición Global</Label>
                                  <Input
                                    id="global_position"
                                    type="text"
                                    readOnly
                                    value="1"
                                    className="bg-muted/40 text-muted-foreground"
                                    data-testid="global-position-auto"
                                  />
                                  <p className="text-xs text-muted-foreground mt-1">Si ganamos, la posición es la primera.</p>
                                </div>
                              ) : (
                                <div>
                                  <Label htmlFor="global_position">Posición Global *</Label>
                                  <Input
                                    id="global_position"
                                    type="number"
                                    min="1"
                                    value={formData.global_position}
                                    onChange={(e) => setFormData({ ...formData, global_position: e.target.value })}
                                    placeholder="1"
                                    required={isResultStage && clientType !== 'typsa'}
                                  />
                                </div>
                              )}
                              
                              <div>
                                <Label htmlFor="technical_position">Posición Técnica *</Label>
                                <Input
                                  id="technical_position"
                                  type="number"
                                  min="1"
                                  value={formData.technical_position}
                                  onChange={(e) => setFormData({ ...formData, technical_position: e.target.value })}
                                  placeholder="1"
                                  required={isResultStage && clientType !== 'typsa'}
                                />
                              </div>
                              
                              <div>
                                <Label htmlFor="economic_position">Posición Económica *</Label>
                                <Input
                                  id="economic_position"
                                  type="number"
                                  min="1"
                                  value={formData.economic_position}
                                  onChange={(e) => setFormData({ ...formData, economic_position: e.target.value })}
                                  placeholder="1"
                                  required={isResultStage && clientType !== 'typsa'}
                                />
                              </div>
                            </>
                          )}
                        </>
                      )}
                      
                      <div className="md:col-span-2">
                        <Label htmlFor="result_comments">Comentarios del Resultado</Label>
                        <Textarea
                          id="result_comments"
                          value={formData.result_comments}
                          onChange={(e) => setFormData({ ...formData, result_comments: e.target.value })}
                          rows={3}
                          placeholder="Comentarios sobre el resultado"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Salto a Producción - Solo para Ganada */}
              {formData.stage === 'Ganada' && (
                <>
                  <Separator />
                  <div className="space-y-4 p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                    <h3 className="font-medium text-sm text-green-700 dark:text-green-400 uppercase tracking-wide flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Salto a Producción
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="tds_project_reference">Proyecto de referencia en TDS</Label>
                        <Input
                          id="tds_project_reference"
                          value={formData.tds_project_reference}
                          onChange={(e) => setFormData({ ...formData, tds_project_reference: e.target.value })}
                          placeholder="Ej: PRY-2024-001"
                          data-testid="tds-project-reference-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="tds_project_task">Tarea dentro del proyecto (si procede)</Label>
                        <Input
                          id="tds_project_task"
                          value={formData.tds_project_task}
                          onChange={(e) => setFormData({ ...formData, tds_project_task: e.target.value })}
                          placeholder="Ej: Fase 1 - Diseño"
                          data-testid="tds-project-task-input"
                        />
                      </div>
                    </div>

                    {/* Ampliaciones de contrato */}
                    <div className="pt-2">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium">Ampliaciones de contrato</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setFormData({
                            ...formData,
                            contract_extensions: [
                              ...(formData.contract_extensions || []),
                              { id: `ext-${Date.now()}`, date: new Date().toISOString().slice(0, 10), amount: '', description: '' },
                            ],
                          })}
                          data-testid="add-contract-extension-btn"
                        >
                          + Añadir ampliación
                        </Button>
                      </div>
                      {(!formData.contract_extensions || formData.contract_extensions.length === 0) ? (
                        <p className="text-xs text-muted-foreground italic">Sin ampliaciones. El importe final = importe de adjudicación.</p>
                      ) : (
                        <div className="space-y-2">
                          {formData.contract_extensions.map((ext, idx) => (
                            <div key={ext.id || idx} className="grid grid-cols-12 gap-2 items-end p-2 border rounded bg-white dark:bg-slate-900/40">
                              <div className="col-span-3">
                                <Label className="text-xs">Fecha</Label>
                                <Input
                                  type="date"
                                  value={ext.date || ''}
                                  onChange={(e) => {
                                    const next = [...formData.contract_extensions];
                                    next[idx] = { ...next[idx], date: e.target.value };
                                    setFormData({ ...formData, contract_extensions: next });
                                  }}
                                  data-testid={`extension-date-${idx}`}
                                />
                              </div>
                              <div className="col-span-3">
                                <Label className="text-xs">Importe (€)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={ext.amount}
                                  onChange={(e) => {
                                    const next = [...formData.contract_extensions];
                                    next[idx] = { ...next[idx], amount: e.target.value };
                                    setFormData({ ...formData, contract_extensions: next });
                                  }}
                                  placeholder="0.00"
                                  data-testid={`extension-amount-${idx}`}
                                />
                              </div>
                              <div className="col-span-5">
                                <Label className="text-xs">Concepto / motivo</Label>
                                <Input
                                  value={ext.description || ''}
                                  onChange={(e) => {
                                    const next = [...formData.contract_extensions];
                                    next[idx] = { ...next[idx], description: e.target.value };
                                    setFormData({ ...formData, contract_extensions: next });
                                  }}
                                  placeholder="Ej: Ampliación alcance"
                                  data-testid={`extension-desc-${idx}`}
                                />
                              </div>
                              <div className="col-span-1 flex justify-end">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const next = formData.contract_extensions.filter((_, i) => i !== idx);
                                    setFormData({ ...formData, contract_extensions: next });
                                  }}
                                  data-testid={`extension-remove-${idx}`}
                                  className="text-red-600 hover:text-red-700"
                                >✕</Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Total final */}
                      {(() => {
                        const base = parseFloat(formData.award_amount) || 0;
                        const sumExt = (formData.contract_extensions || []).reduce((acc, e) => acc + (parseFloat(e.amount) || 0), 0);
                        const total = base + sumExt;
                        return (
                          <div className="mt-3 flex items-center justify-end gap-6 text-sm border-t pt-2">
                            <span className="text-muted-foreground">Adjudicación: <strong className="text-foreground">{base.toLocaleString('es-ES', { minimumFractionDigits: 0 })} €</strong></span>
                            <span className="text-muted-foreground">Ampliaciones: <strong className="text-foreground">{sumExt.toLocaleString('es-ES', { minimumFractionDigits: 0 })} €</strong></span>
                            <span className="font-bold text-green-700 text-base" data-testid="contract-final-total">
                              Final: {total.toLocaleString('es-ES', { minimumFractionDigits: 0 })} €
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </>
              )}

              {/* Motivo de Descarte - Only for Descartada (Feb 2026 PR-B) */}
              {isDescartada && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                      Motivo del Descarte (Obligatorio)
                    </h3>

                    <div>
                      <Label htmlFor="discard_reason_primary">Motivo principal *</Label>
                      <Select
                        value={formData.discard_reason_primary || ''}
                        onValueChange={(v) => setFormData({ ...formData, discard_reason_primary: v })}
                      >
                        <SelectTrigger id="discard_reason_primary" data-testid="discard-primary-select">
                          <SelectValue placeholder="Selecciona un motivo" />
                        </SelectTrigger>
                        <SelectContent>
                          {DISCARD_REASON_OPTIONS.map(o => (
                            <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="discard_reason_secondary">Motivo secundario (opcional)</Label>
                      <Select
                        value={formData.discard_reason_secondary || 'none'}
                        onValueChange={(v) => setFormData({ ...formData, discard_reason_secondary: v === 'none' ? '' : v })}
                      >
                        <SelectTrigger id="discard_reason_secondary" data-testid="discard-secondary-select">
                          <SelectValue placeholder="Sin motivo secundario" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin motivo secundario</SelectItem>
                          {DISCARD_REASON_OPTIONS.map(o => (
                            <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="discard_reason">Detalle del motivo *</Label>
                      <Textarea
                        id="discard_reason"
                        value={formData.discard_reason}
                        onChange={(e) => setFormData({ ...formData, discard_reason: e.target.value })}
                        rows={3}
                        placeholder="Explique con más detalle el motivo del descarte"
                        required={isDescartada}
                      />
                    </div>

                    {/* Feb 2026 (PR-B B4): follow-up task creation confirmation */}
                    <div className="rounded-md border p-3 bg-blue-50/40">
                      <div className="flex items-start gap-2">
                        <input
                          id="create_followup"
                          type="checkbox"
                          className="mt-1"
                          data-testid="create-followup-checkbox"
                          checked={formData.create_followup_task ?? (clientType === 'publica')}
                          onChange={(e) => setFormData({ ...formData, create_followup_task: e.target.checked })}
                        />
                        <div className="flex-1">
                          <Label htmlFor="create_followup" className="cursor-pointer text-sm">
                            Crear tarea de seguimiento de adjudicación
                          </Label>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {clientType === 'publica'
                              ? 'Pública: recomendado. Se creará una tarea con fecha prevista de resolución.'
                              : 'Privada/TYPSA: por defecto desactivado, ya que el resultado no es público.'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="expected_resolution_date">Fecha prevista de resolución / adjudicación</Label>
                      <Input
                        id="expected_resolution_date"
                        type="date"
                        value={formData.expected_resolution_date || ''}
                        onChange={(e) => setFormData({ ...formData, expected_resolution_date: e.target.value })}
                        data-testid="expected-resolution-date-input"
                      />
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Sugerido: {clientType === 'publica' ? 'entrega + 90 días' : 'entrega + 30 días'}. Editable.
                      </p>
                    </div>

                    {/* Follow-up switch: only visible in Privada/TYPSA per B4.3 */}
                    {clientType !== 'publica' && (
                      <div className="rounded-md border p-3 bg-slate-50/60">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label htmlFor="follow_up_active" className="cursor-pointer text-sm">
                              Hacer seguimiento del resultado
                            </Label>
                            <p className="text-xs text-muted-foreground">
                              Al activarlo, la oportunidad entra en el contador de adjudicaciones pendientes y se creará una tarea (si marcaste la casilla).
                            </p>
                          </div>
                          <input
                            id="follow_up_active"
                            type="checkbox"
                            className="w-4 h-4"
                            data-testid="follow-up-active-toggle"
                            checked={!!formData.follow_up_active}
                            onChange={(e) => setFormData({ ...formData, follow_up_active: e.target.checked })}
                          />
                        </div>
                      </div>
                    )}

                    {/* Responsable del seguimiento (Descartada) — editable en la ficha
                        (matriz FTF Feb 2026). Visible siempre que Descartada, con contexto por scope. */}
                    <div>
                      <Label htmlFor="follow_up_owner">Responsable del seguimiento</Label>
                      <Select
                        value={formData.follow_up_owner || 'none'}
                        onValueChange={(v) => setFormData({ ...formData, follow_up_owner: v === 'none' ? '' : v })}
                      >
                        <SelectTrigger id="follow_up_owner" data-testid="follow-up-owner-select">
                          <SelectValue placeholder="Sin asignar" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin asignar</SelectItem>
                          {editorUsers?.map((u) => (
                            <SelectItem key={u.email} value={u.name}>{u.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        {clientType === 'publica'
                          ? 'En Pública el seguimiento se activa automáticamente al descartar.'
                          : 'Solo se usa si activaste el seguimiento del resultado.'}
                      </p>
                    </div>
                  </div>
                </>
              )}

              <div className="pt-4 shrink-0 sticky bottom-0 bg-background border-t mt-4 -mx-6 px-6 pb-2">
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-opportunity-btn">
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={loading} data-testid="save-opportunity-btn">
                    {loading ? 'Guardando...' : 'Guardar'}
                  </Button>
                </DialogFooter>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* State Change Confirmation Dialog */}
      <StateChangeDialog
        open={stateChangeDialogOpen}
        onOpenChange={setStateChangeDialogOpen}
        fromStage={originalStage}
        toStage={pendingStageChange}
        currentOwner={formData.owner}
        clientType={clientType}
        currentData={formData}
        onConfirm={handleStateChangeConfirm}
        onCancel={handleStateChangeCancel}
        editorUsers={editorUsers}
      />

      {/* Quick Company Dialog */}
      <QuickCompanyDialog
        open={quickCompanyDialogOpen}
        onOpenChange={setQuickCompanyDialogOpen}
        companyType={clientType}
        onSuccess={handleQuickCompanySuccess}
      />

      {/* Quick Contact Dialog */}
      <QuickContactDialog
        open={quickContactDialogOpen}
        onOpenChange={setQuickContactDialogOpen}
        companyId={formData.company_id}
        companyName={companies.find(c => c.id === formData.company_id)?.name}
        onSuccess={handleQuickContactSuccess}
      />
    </>
  );
};

export default OpportunityDialog;
